import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useCVContext } from '@/context/CVContext';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import Header from '@/components/Header';
import { ArrowLeft, ArrowRight, Brain, Sparkles, Loader2, HelpCircle, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

export default function PersonalityTest() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { state } = useCVContext();
  const profile = state?.profile;
  
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const likertOptions = language === 'fr' ? [
    { value: 1, label: "Pas du tout d'accord" },
    { value: 2, label: "Pas d'accord" },
    { value: 3, label: "Neutre" },
    { value: 4, label: "D'accord" },
    { value: 5, label: "Tout à fait d'accord" }
  ] : [
    { value: 1, label: "Strongly disagree" },
    { value: 2, label: "Disagree" },
    { value: 3, label: "Neutral" },
    { value: 4, label: "Agree" },
    { value: 5, label: "Strongly agree" }
  ];

  useEffect(() => {
    loadQuestions();
  }, [language]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/personality/questions?language=${language}`);
      if (response.data.questions && response.data.questions.length > 0) {
        setQuestions(response.data.questions);
      } else {
        toast.error(language === 'fr' ? 'Erreur lors du chargement des questions' : 'Error loading questions');
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement des questions' : 'Error loading questions');
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = useCallback((questionId, score) => {
    setResponses(prev => {
      const newResponses = { ...prev, [questionId]: score };
      return newResponses;
    });
    // NO auto-advance - user must click "Next" button
  }, []);

  // Navigation handlers
  const goToNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  // Calculate these values based on current state - MUST be defined before isCurrentAnswered
  const currentQuestionData = questions[currentQuestion];
  const currentQuestionId = currentQuestionData?.id;
  const answeredCount = Object.keys(responses).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;
  const isLastQuestion = currentQuestion === questions.length - 1;
  const allAnswered = questions.length > 0 && answeredCount >= questions.length;
  const unansweredQuestions = questions.filter(q => responses[q.id] === undefined);
  
  // Check if current question is answered - uses currentQuestionData defined above
  const isCurrentAnswered = currentQuestionData && responses[currentQuestionData.id] !== undefined;

  const handleSubmit = async () => {
    const currentAnsweredCount = Object.keys(responses).length;
    
    if (currentAnsweredCount < questions.length) {
      // Find first unanswered question
      const firstUnanswered = questions.findIndex(q => responses[q.id] === undefined);
      if (firstUnanswered !== -1) {
        setCurrentQuestion(firstUnanswered);
        toast.error(language === 'fr' 
          ? `Veuillez répondre à toutes les questions (${questions.length - currentAnsweredCount} restantes)`
          : `Please answer all questions (${questions.length - currentAnsweredCount} remaining)`);
      }
      return;
    }

    setSubmitting(true);
    try {
      const formattedResponses = questions.map(q => ({
        question_id: q.id,
        score: responses[q.id]
      }));

      const response = await api.post('/personality/submit', {
        profile_id: profile?.id || null,
        responses: formattedResponses,
        language: language
      });

      if (response.data.success) {
        // Save to localStorage for session persistence
        localStorage.setItem('personalityResult', JSON.stringify({
          result: response.data.result,
          retrievalCode: response.data.retrieval_code,
          completedAt: new Date().toISOString()
        }));

        navigate('/personality-result', { 
          state: { 
            result: response.data.result,
            retrievalCode: response.data.retrieval_code 
          } 
        });
      } else {
        throw new Error('Submit failed');
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast.error(language === 'fr' 
        ? 'Erreur lors de la soumission. Veuillez réessayer.'
        : 'Error submitting. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Help Modal
  const HelpModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowHelp(false)}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-purple-500" />
            {language === 'fr' ? 'Comment répondre ?' : 'How to answer?'}
          </h3>
          <button onClick={() => setShowHelp(false)} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4 text-sm text-gray-600">
          <div className="p-3 bg-purple-50 rounded-lg">
            <p className="font-medium text-gray-800 mb-2">
              {language === 'fr' ? '📝 Répondez spontanément' : '📝 Answer spontaneously'}
            </p>
            <p>{language === 'fr' 
              ? "Il n'y a pas de bonnes ou mauvaises réponses. Choisissez ce qui vous correspond le mieux dans un contexte professionnel."
              : "There are no right or wrong answers. Choose what best describes you in a professional context."}
            </p>
          </div>
          
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="font-medium text-gray-800 mb-2">
              {language === 'fr' ? '⏱️ Prenez votre temps' : '⏱️ Take your time'}
            </p>
            <p>{language === 'fr' 
              ? "Lisez chaque affirmation attentivement et réfléchissez à comment vous réagissez habituellement."
              : "Read each statement carefully and think about how you usually react."}
            </p>
          </div>
          
          {unansweredQuestions.length > 0 && unansweredQuestions.length < questions.length && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {language === 'fr' ? 'Questions manquantes' : 'Missing questions'}
              </p>
              <p className="text-amber-700">
                {language === 'fr' 
                  ? `Il vous reste ${unansweredQuestions.length} question(s) sans réponse. Utilisez les points de navigation en bas pour les retrouver (points gris = non répondues).`
                  : `You have ${unansweredQuestions.length} unanswered question(s). Use the navigation dots at the bottom to find them (gray dots = unanswered).`}
              </p>
            </div>
          )}
        </div>
        
        <Button onClick={() => setShowHelp(false)} className="w-full mt-4 bg-purple-500 hover:bg-purple-600">
          {language === 'fr' ? 'Compris !' : 'Got it!'}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'rgb(241, 245, 249)' }}>
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">{language === 'fr' ? 'Chargement du test...' : 'Loading test...'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestionData) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'rgb(241, 245, 249)' }}>
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-gray-600 mb-4">{language === 'fr' ? 'Erreur de chargement' : 'Loading error'}</p>
            <Button onClick={loadQuestions}>{language === 'fr' ? 'Réessayer' : 'Retry'}</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'rgb(241, 245, 249)' }}>
      <Header />
      {showHelp && <HelpModal />}

      <main className="relative max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{language === 'fr' ? 'Retour' : 'Back'}</span>
            </button>
            
            {/* Help Button */}
            <button
              onClick={() => setShowHelp(true)}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-800 transition-colors text-sm bg-purple-50 px-3 py-1.5 rounded-full"
            >
              <HelpCircle className="w-4 h-4" />
              <span>{language === 'fr' ? 'Aide' : 'Help'}</span>
            </button>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                {language === 'fr' ? 'Test de Personnalité' : 'Personality Test'}
              </h1>
              <p className="text-gray-500 text-sm">
                {language === 'fr' ? 'Découvrez votre "Soft Power"' : 'Discover your "Soft Power"'}
              </p>
            </div>
          </div>

          {/* Progress */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-500">
                {language === 'fr' ? 'Progression' : 'Progress'}
              </span>
              <span className="text-sm font-medium text-gray-800">
                {answeredCount}/{questions.length}
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 mb-6 shadow-sm">
          <div className="text-sm text-purple-600 mb-2 font-medium">
            Question {currentQuestion + 1}/{questions.length}
          </div>
          
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6 leading-relaxed">
            {currentQuestionData.text}
          </h2>

          {/* Likert Scale */}
          <div className="space-y-2">
            {likertOptions.map((option) => {
              const isSelected = responses[currentQuestionId] === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => handleResponse(currentQuestionId, option.value)}
                  className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left
                    ${isSelected
                      ? 'border-purple-500 bg-purple-50 text-gray-900'
                      : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
                      ${isSelected
                        ? 'border-purple-500 bg-purple-500'
                        : 'border-gray-300'
                      }`}
                    >
                      {isSelected && (
                        <div className="w-2 h-2 bg-white rounded-full" />
                      )}
                    </div>
                    <span className="font-medium text-sm">{option.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={goToPreviousQuestion}
            disabled={currentQuestion === 0}
            className="border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Précédent' : 'Previous'}
          </Button>

          {isLastQuestion ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !allAnswered}
              className={`${allAnswered 
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600' 
                : 'bg-gray-300 cursor-not-allowed'} text-white`}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {language === 'fr' ? 'Analyse...' : 'Analyzing...'}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Voir mes résultats' : 'See my results'}
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={goToNextQuestion}
              disabled={!isCurrentAnswered}
              className={`${isCurrentAnswered 
                ? 'bg-purple-500 hover:bg-purple-600' 
                : 'bg-gray-300 cursor-not-allowed'} text-white`}
            >
              {language === 'fr' ? 'Suivant' : 'Next'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Message when answer required */}
        {!isCurrentAnswered && !isLastQuestion && (
          <div className="mt-3 text-center">
            <p className="text-amber-600 text-sm">
              {language === 'fr' 
                ? '👆 Veuillez sélectionner une réponse pour continuer' 
                : '👆 Please select an answer to continue'}
            </p>
          </div>
        )}

        {/* Quick Navigation Dots */}
        <div className="flex justify-center gap-1.5 mt-6 flex-wrap">
          {questions.map((q, idx) => {
            const isAnswered = responses[q.id] !== undefined;
            const isCurrent = idx === currentQuestion;
            return (
              <button
                key={q.id}
                onClick={() => setCurrentQuestion(idx)}
                className={`w-3 h-3 rounded-full transition-all
                  ${isCurrent
                    ? 'bg-purple-500 scale-125'
                    : isAnswered
                      ? 'bg-green-500'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                title={`Question ${idx + 1}${isAnswered ? ' ✓' : ''}`}
              />
            );
          })}
        </div>

        {/* Missing questions warning */}
        {isLastQuestion && !allAnswered && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-center">
            <p className="text-amber-700 text-sm flex items-center justify-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {language === 'fr' 
                ? `${questions.length - answeredCount} question(s) sans réponse. Cliquez sur les points gris ci-dessus pour les compléter.`
                : `${questions.length - answeredCount} unanswered question(s). Click the gray dots above to complete them.`}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
